import { useCurrency } from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@shared/ui/collapsible";
import { Progress } from "@shared/ui/progress";
import { Tabs } from "@shared/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Clock,
  Edit,
  PlayCircle,
} from "lucide-react";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import {
  useBillCycleConsumerDashboard,
  useBillCycleDetails,
  useConsumerList,
  useMonthlyStats,
} from "../../hooks";
import { BillCycleConsumerList } from "../BillCycleConsumerList";
import { BillCycleRunsTable } from "../BillCycleRunsTable";

// Custom tooltip component for charts
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) {
    return null;
  }
  return (
    <div className="bg-white p-2 border border-gray-200 rounded-md shadow-md text-xs">
      <p className="font-medium">{label}</p>
      {payload.map((entry: any, index: number) => {
        const dataKey = entry.dataKey;
        const value = entry.value;
        const formattedValue =
          dataKey === "billed"
            ? `$${value.toLocaleString()}`
            : `${value.toLocaleString()}`;
        return (
          <p key={index} className="text-gray-700">
            {dataKey === "totalRevenue" ? "Billed Amount: " : "Consumption: "}
            <span className="font-medium">{formattedValue}</span>
          </p>
        );
      })}
    </div>
  );
};

const BillCycleDetail = () => {
  const params = useParams();
  const id = params?.id;
  const navigate = useNavigate();
  const billCycleId = id ? parseInt(id) : 0;
  const [activeChart, setActiveChart] = useState<"revenue" | "consumption">(
    "revenue"
  );
  const currency = useCurrency();

  const formatCurrency = (
    currency: string,
    amount: number | string | undefined | null
  ): string => {
    if (amount === undefined || amount === null || amount === "") {
      return "NA";
    }
    return `${currency} ${amount}`;
  };

  const { data: billCycleDetails, isLoading: isLoadingDetails } =
    useBillCycleDetails(billCycleId);

  const { data: consumerDashboard } = useBillCycleConsumerDashboard({
    bill_cycle_id: billCycleId,
  });
  const [consumerQueryParams, setConsumerQueryParams] = useState({
    page: 1,
    status: "",
    search_data: "",
    area: [],
    sub_area: [],
  });

  const { data: consumerList } = useConsumerList(consumerQueryParams, id, true);

  const { data: monthlyData } = useMonthlyStats(billCycleId);
  console.log(monthlyData);
  const [selectedConsumers, setSelectedConsumers] = useState<string[]>([]);

  const handleConsumersSelectionChange = useCallback((selectedIds: any) => {
    setSelectedConsumers(selectedIds);
  }, []);
  const [isConsumerStatsOpen, setIsConsumerStatsOpen] = useState(true);
  const [isFinancialSummaryOpen, setIsFinancialSummaryOpen] = useState(true);
  const [isConsumptionSummaryOpen, setIsConsumptionSummaryOpen] =
    useState(true);

  const handleConsumerFiltersChange = useCallback((newParams) => {
    setConsumerQueryParams((prev) => ({
      ...prev,
      ...newParams,
    }));
  }, []);

  const tabComponents = {
    runs: {
      label: "Run History",
      component: <BillCycleRunsTable />,
      loading: isLoadingDetails,
    },
    consumers: {
      label: "Consumer Bill List",
      component: (
        <BillCycleConsumerList
          onSetFilters={handleConsumerFiltersChange} // Add this prop
          consumersList={consumerList}
          selectedConsumers={selectedConsumers}
          onSelectionChange={handleConsumersSelectionChange}
          action={"view"}
          queryParams={consumerQueryParams} // Pass current params
        />
      ),
      loading: isLoadingDetails,
    },
  };

  const handleEdit = () => {
    navigate(`/bx/bill-setup/billcycle/action/edit/${params?.id}`);
  };

  const handleRun = () => {
    navigate(`/bx/create-bill-generation?billCycleId=${params?.id}`);
  };

  return (
    <div className="">
      <div className="">
        {/* Header & Navigation */}
        <header className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <div>
              <h1 className="page-subheading">
                {billCycleDetails?.billCycleName}
              </h1>
              <p className="page-subtitle">Bill Cycle Details</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
            <Button
              variant="outline"
              className="flex gap-1 text-xs sm:text-sm"
              onClick={handleEdit}
            >
              <Edit size={16} />
              <span className="hidden xs:inline">Edit</span>
            </Button>
            <Button
              className="flex gap-1 text-xs sm:text-sm"
              onClick={handleRun}
            >
              <PlayCircle size={16} />
              <span>Run Now</span>
            </Button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="lg:col-span-7">
            <Card className="mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  Last Run Summary
                </CardTitle>
                <p className="text-xs sm:text-sm text-gray-500">
                  Details of the most recent bill cycle run
                </p>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-500">
                        Run Date
                      </p>
                      <p className="font-medium text-sm sm:text-base">
                        {billCycleDetails?.lastRunDate ?? "NA"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-500">
                        Initiated By
                      </p>
                      <p className="font-medium text-sm sm:text-base">
                        {billCycleDetails?.initiatedBy ?? "NA"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-500">
                        Days Between Runs
                      </p>
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        <p className="font-medium text-sm sm:text-base">
                          {billCycleDetails?.daysSinceLastRun ?? "NA"}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-500">
                        Actual vs Estimated
                      </p>
                      <p className="font-medium text-sm sm:text-base">
                        {billCycleDetails?.actualVsEstimated.actualPercentage ??
                          "NA"}
                        {"% "}
                        Actual
                      </p>
                    </div>
                  </div>
                </div>

                <hr className="border-gray-100" />

                {/* Consumer Stats - Collapsible */}
                <Collapsible
                  open={isConsumerStatsOpen}
                  onOpenChange={setIsConsumerStatsOpen}
                  className="overflow-hidden"
                >
                  <CollapsibleTrigger asChild>
                    <div className="flex justify-between items-center cursor-pointer hover:bg-gray-50 -mx-6 px-6 py-2 rounded">
                      <div>
                        <h3 className="card-heading-text">
                          Billing Summary
                        </h3>
                        <p className="card-subheading-text">
                          Statistics about bills processed in this bill cycle
                        </p>
                      </div>
                      {isConsumerStatsOpen ? (
                        <ChevronUp size={18} />
                      ) : (
                        <ChevronDown size={18} />
                      )}
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="pt-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="cursor-help">
                                <p className="text-xs sm:text-sm text-gray-500">
                                  Processed
                                </p>
                                <p className="font-medium text-base sm:text-lg">
                                  {billCycleDetails?.processedBills ?? "NA"}
                                </p>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">
                                Total number of consumers processed in this run
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="cursor-help">
                                <p className="text-xs sm:text-sm text-gray-500">
                                  Actual Bills
                                </p>
                                <p className="font-medium text-base sm:text-lg text-green-600">
                                  {billCycleDetails?.actualVsEstimated
                                    .actualBillsCount ?? "NA"}
                                </p>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">
                                Number of bills generated with actual meter
                                readings
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="cursor-help">
                                <p className="text-xs sm:text-sm text-gray-500">
                                  Estimated Bills
                                </p>
                                <p className="font-medium text-base sm:text-lg text-amber-600">
                                  {billCycleDetails?.actualVsEstimated
                                    .estimatedBillsCount ?? "NA"}
                                </p>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">
                                Number of bills generated with estimated
                                readings
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                <hr className="border-gray-100" />

                <Collapsible
                  open={isFinancialSummaryOpen}
                  onOpenChange={setIsFinancialSummaryOpen}
                  className="overflow-hidden"
                >
                  <CollapsibleTrigger asChild>
                    <div className="flex justify-between items-center cursor-pointer hover:bg-gray-50 -mx-6 px-6 py-2 rounded">
                      <div>
                        <h3 className="card-heading-text">
                          Financial Summary
                        </h3>
                        <p className="card-subheading-text">
                          Financial data for the completed bill cycle run
                        </p>
                      </div>
                      {isFinancialSummaryOpen ? (
                        <ChevronUp size={18} />
                      ) : (
                        <ChevronDown size={18} />
                      )}
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="pt-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500">
                            Billed Amount
                          </p>
                          <p className="font-medium text-base sm:text-lg">
                            {formatCurrency(
                              currency,
                              billCycleDetails?.billedAmount
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500">
                            Avg Bill
                          </p>
                          <p className="font-medium text-base sm:text-lg">
                            {formatCurrency(
                              currency,
                              billCycleDetails?.avgBillAmount
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500">
                            Outstanding
                          </p>
                          <p className="font-medium text-base sm:text-lg">
                            {formatCurrency(
                              currency,
                              billCycleDetails?.outstanding
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500">
                            Late Fees
                          </p>
                          <p className="font-medium text-base sm:text-lg text-red-600">
                            {formatCurrency(
                              currency,
                              billCycleDetails?.lateFees
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                <hr className="border-gray-100" />

                {/* Location Information - Collapsible */}
                <Collapsible
                  open={isConsumptionSummaryOpen}
                  onOpenChange={setIsConsumptionSummaryOpen}
                  className="overflow-hidden"
                >
                  <CollapsibleTrigger asChild>
                    <div className="flex justify-between items-center cursor-pointer hover:bg-gray-50 -mx-6 px-6 py-2 rounded">
                      <div>
                        <h3 className="card-heading-text">
                          Consumption Summary
                        </h3>
                        <p className="card-subheading-text">
                          Water usage metrics across all consumers in this cycle
                        </p>
                      </div>
                      {isConsumptionSummaryOpen ? (
                        <ChevronUp size={18} />
                      ) : (
                        <ChevronDown size={18} />
                      )}
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="pt-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500">
                            Total Consumption
                          </p>
                          <p className="font-medium text-sm sm:text-base">
                            {billCycleDetails?.totalConsumption ?? "NA"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500">
                            Avg Consumption
                          </p>
                          <p className="font-medium text-sm sm:text-base">
                            {billCycleDetails?.avgConsumption ?? "NA"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500">
                            High Consumption
                          </p>
                          <p className="font-medium text-sm sm:text-base text-amber-600">
                            {billCycleDetails?.highConsumptionCount ?? "NA"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500">
                            Low Consumption
                          </p>
                          <p className="font-medium text-sm sm:text-base text-blue-600">
                            {billCycleDetails?.lowConsumptionCount ?? "NA"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Data Visualization Group */}
          <div className="lg:col-span-5">
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Monthly Trend</CardTitle>
                <p className="text-xs sm:text-sm text-gray-500">
                  Five month historical view of revenue and consumption
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Revenue / Consumption Trend Chart */}
                <div>
                  <div className="flex items-center justify-center sm:justify-end mb-2">
                    <div className="flex items-center bg-gray-100 rounded-md p-1">
                      <button
                        className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md ${
                          activeChart === "revenue" ? "bg-white shadow-sm" : ""
                        }`}
                        onClick={() => setActiveChart("revenue")}
                      >
                        Revenue
                      </button>
                      <button
                        className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md ${
                          activeChart === "consumption"
                            ? "bg-white shadow-sm"
                            : ""
                        }`}
                        onClick={() => setActiveChart("consumption")}
                      >
                        Consumption
                      </button>
                    </div>
                  </div>

                  <div className="h-[200px] sm:h-[260px]">
                    {monthlyData?.monthlyStats?.length > 0 ? (
                      activeChart === "revenue" ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={monthlyData.monthlyStats}
                            margin={{ bottom: 30 }}
                          >
                            <CartesianGrid strokeDasharray="2 2" />
                            <XAxis
                              dataKey="month"
                              interval={0} // Show all ticks, no skipping
                              tick={({ x, y, payload }) => {
                                const [month, year] = payload.value.split(" ");
                                return (
                                  <text
                                    x={x}
                                    y={y + 10}
                                    textAnchor="middle"
                                    fill="#666"
                                    fontSize={9} // smaller font
                                  >
                                    <tspan x={x} dy="0">
                                      {month}
                                    </tspan>
                                    <tspan x={x} dy="10">
                                      {year}
                                    </tspan>
                                  </text>
                                );
                              }}
                            />

                            <YAxis />
                            <RechartsTooltip content={<CustomTooltip />} />
                            <Legend />
                            <Bar
                              dataKey="totalRevenue"
                              name="Billed Amount"
                              fill="#4f46e5"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={monthlyData.monthlyStats}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="month"
                              interval={0} // Show all ticks, no skipping
                              tick={({ x, y, payload }) => {
                                const [month, year] = payload.value.split(" ");
                                return (
                                  <text
                                    x={x}
                                    y={y + 10}
                                    textAnchor="middle"
                                    fill="#666"
                                    fontSize={9} // smaller font
                                  >
                                    <tspan x={x} dy="0">
                                      {month}
                                    </tspan>
                                    <tspan x={x} dy="10">
                                      {year}
                                    </tspan>
                                  </text>
                                );
                              }}
                            />
                            <YAxis />
                            <RechartsTooltip content={<CustomTooltip />} />
                            <Legend />
                            <Bar
                              dataKey="totalConsumption"
                              name="Consumption"
                              fill="#06b6d4"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      )
                    ) : (
                      <ul className="flex items-center justify-center h-full text-sm text-gray-500 list-disc pl-5">
                        <li>No monthly data available</li>
                        {/* You can add more <li> items if needed */}
                      </ul>
                    )}
                  </div>
                </div>

                <hr className="border-gray-100" />

                {/* Consumer Categories */}
                <div>
                  <h3 className="card-heading-text">
                    Consumer Breakdown by Category
                  </h3>
                  <p className="card-subheading-text">
                    Distribution of consumers by their service classification
                  </p>

                  <div className="space-y-4 pt-4">
                    {consumerDashboard?.categoryData?.map((category) => (
                      <div key={category.category} className="space-y-1">
                        <div className="flex justify-between text-xs sm:text-sm">
                          <span>{category.category}</span>
                          <span className="font-medium">
                            {category.consumerCount} ({category.percentage}%)
                          </span>
                        </div>
                        <Progress value={category.percentage} className="h-2" />
                      </div>
                    ))}
                  </div>
                </div>

                <hr className="border-gray-100" />

                <div>
                  <h3 className="card-heading-text">
                    Actual vs Estimated Bills Ratio
                  </h3>
                  <p className="card-subheading-text">
                    Average ratio of actual to estimated bill readings from
                    cycle creation
                  </p>

                  <div className="flex justify-center items-center space-x-6 py-4">
                    <div className="text-center">
                      <div className="card-value-text text-green-600">
                        {billCycleDetails?.actualVsEstimated.actualPercentage !=
                        null
                          ? `${billCycleDetails.actualVsEstimated.actualPercentage}%`
                          : "NA"}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-500 mt-1">
                        Actual
                      </div>
                    </div>

                    <div className="h-10 border-r border-gray-200"></div>

                    <div className="text-center">
                      <div className="card-value-text font-bold text-amber-500">
                        {billCycleDetails?.actualVsEstimated
                          .estimatedPercentage != null
                          ? `${billCycleDetails.actualVsEstimated.estimatedPercentage}%`
                          : "NA"}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-500 mt-1">
                        Estimated
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-center text-gray-500">
                    Based on historical data since cycle creation
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Lower Section - Tabbed View using new Tabs component */}
      <Tabs
        defaultValue="runs"
        tabComponents={tabComponents}
        // className="
        tabsListClassName="overflow-x-auto scrollbar-none grid grid-cols-2"
        urlMapping={{
          runs: "runs",
          consumers: "consumers",
        }}
      />
    </div>
  );
};

export default BillCycleDetail;
